import api from '../../services/api';
import { ResponseData } from '../../types/response/ResponseData';
import ResponseError from '../../types/response/ResponseError';
import {
    AddMerchantRequest,
    Merchant,
    MerchantStatus,
    MerchantWorkflow,
} from '../../types/onboarding/Merchant';
import { MerchantWorkflowSession } from '../../types/onboarding/MerchantWorkflowSession';
import { MerchantBankAccount } from '../../types/onboarding/MerchantBankAccount';
import {
    OnboardedStore,
    OnboardingRequest,
    OnboardingResponse,
    OnboardingStoreResult,
    OnboardStoreInput,
    getStoreTemplate,
    STORE_SALES_CATEGORY,
} from '../../types/onboarding/OnboardStore';
import { getOnboardingCountryName } from '../../constants/onboardingCountries';

/** Onboarding creates merchant, sub-organization and store in sequence, so it needs longer than the default 20 s. */
export const ONBOARDING_REQUEST_TIMEOUT_IN_MILLISECONDS = 60_000;
export const listMerchants = async (
    organizationId: string,
    listMerchantsStarted: () => void,
    listMerchantsSucceeded: (merchants: Merchant[]) => void,
    listMerchantsFailed: (error: ResponseError) => void
) => {
    listMerchantsStarted();

    try {
        const response = await api.get<ResponseData<Merchant[]>>(
            `organizations/${organizationId}/merchants`
        );

        listMerchantsSucceeded(response.data ?? []);
    } catch (error) {
        listMerchantsFailed(error as ResponseError);
    }
};

/**
 * Adds a merchant to an organization (POST organizations/{id}/merchants). The merchant gets status Pending; the
 * backend resolves it in Bits KYC (creating it there with the legal name when missing) and starts its KYC and bank
 * account verification, which is why it may take longer than the default timeout. Not retried: the request is not
 * idempotent, so a retry after a timeout would fail as a duplicate merchant. Agreements are signed outside Merchant Hub
 * for now, so the merchant is added as already signed.
 */
export const addMerchant = async (
    organizationId: string,
    request: AddMerchantRequest,
    addMerchantStarted: () => void,
    addMerchantSucceeded: (merchant: Merchant) => void,
    addMerchantFailed: (error: ResponseError) => void
) => {
    addMerchantStarted();

    try {
        const response = await api.postWithResponseType<
            AddMerchantRequest & { alreadySigned: boolean },
            ResponseData<Merchant>
        >(
            `organizations/${organizationId}/merchants`,
            { ...request, alreadySigned: true },
            undefined,
            { timeout: ONBOARDING_REQUEST_TIMEOUT_IN_MILLISECONDS, retries: 0 }
        );

        addMerchantSucceeded(response.data);
    } catch (error) {
        addMerchantFailed(error as ResponseError);
    }
};

/**
 * Lists the verified bank accounts of a merchant
 * (GET organizations/{id}/merchants/{countryCode}/{organizationNumber}/bankaccounts), to pick the
 * settlement account when onboarding a store. Only accounts verified through Bits are returned.
 */
export const listMerchantBankAccounts = async (
    organizationId: string,
    merchant: Merchant,
    listBankAccountsStarted: (merchant: Merchant) => void,
    listBankAccountsSucceeded: (merchant: Merchant, bankAccounts: MerchantBankAccount[]) => void,
    listBankAccountsFailed: (error: ResponseError) => void
) => {
    listBankAccountsStarted(merchant);

    try {
        const response = await api.get<ResponseData<MerchantBankAccount[]>>(
            `organizations/${organizationId}/merchants/${encodeURIComponent(
                merchant.countryCode
            )}/${encodeURIComponent(merchant.organizationNumber)}/bankaccounts`
        );

        listBankAccountsSucceeded(merchant, response.data ?? []);
    } catch (error) {
        listBankAccountsFailed(error as ResponseError);
    }
};

/**
 * Sets the status of a merchant (PUT organizations/{id}/merchants/{countryCode}/{organizationNumber}/status).
 * The backend requires organization write permission; the UI only offers it to internal users.
 */
export const setMerchantStatus = async (
    organizationId: string,
    merchant: Merchant,
    status: MerchantStatus,
    setMerchantStatusStarted: (merchant: Merchant) => void,
    setMerchantStatusSucceeded: (merchant: Merchant) => void,
    setMerchantStatusFailed: (error: ResponseError) => void
) => {
    setMerchantStatusStarted(merchant);

    try {
        const response = (await api.put(
            `organizations/${organizationId}/merchants/${encodeURIComponent(
                merchant.countryCode
            )}/${encodeURIComponent(merchant.organizationNumber)}/status`,
            { status }
        )) as ResponseData<Merchant> | undefined;

        setMerchantStatusSucceeded(response?.data ?? { ...merchant, status });
    } catch (error) {
        setMerchantStatusFailed(error as ResponseError);
    }
};

/**
 * Opens one of the Bits workflows of a merchant, KYC or bank account verification
 * (POST organizations/{id}/merchants/{countryCode}/{organizationNumber}/workflows/{workflow}/session).
 * The backend issues a new session token for the application started when the merchant was added; the session
 * is short-lived, so one is requested per click. Requires write on all stores of the organization.
 */
export const startMerchantWorkflow = async (
    organizationId: string,
    merchant: Merchant,
    workflow: MerchantWorkflow,
    startWorkflowStarted: (workflow: MerchantWorkflow, merchant: Merchant) => void,
    startWorkflowSucceeded: (
        workflow: MerchantWorkflow,
        merchant: Merchant,
        session: MerchantWorkflowSession
    ) => void,
    startWorkflowFailed: (workflow: MerchantWorkflow, error: ResponseError) => void
) => {
    startWorkflowStarted(workflow, merchant);

    try {
        const response = await api.postWithResponseType<
            Record<string, never>,
            ResponseData<MerchantWorkflowSession>
        >(
            `organizations/${organizationId}/merchants/${encodeURIComponent(
                merchant.countryCode
            )}/${encodeURIComponent(merchant.organizationNumber)}/workflows/${workflow}/session`,
            {},
            undefined,
            { retries: 0 }
        );

        startWorkflowSucceeded(workflow, merchant, response.data);
    } catch (error) {
        startWorkflowFailed(workflow, error as ResponseError);
    }
};

/**
 * Builds the POST onboarding body from the looked-up merchant and the modal input.
 * One store with the template of the merchant (see getStoreTemplate) and STORE_SALES_CATEGORY sales category, on the
 * selected allowed URL of the merchant, settled to the selected verified bank account of the merchant, reachable
 * with the given email and phone number printed on its invoices.
 */
export const buildOnboardingRequest = (
    merchant: Merchant,
    input: OnboardStoreInput
): OnboardingRequest => ({
    ...(input.groupCode ? { groupCode: input.groupCode } : {}),
    merchant: {
        name: merchant.name,
        organizationNumber: merchant.organizationNumber,
        vatRegistrationCode: merchant.vatRegistrationCode,
        countryCode: merchant.countryCode,
        contactInformation: merchant.contactInformation,
        invoiceInformation: merchant.invoiceInformation,
    },
    stores: [
        {
            template: getStoreTemplate(merchant),
            salesSegment: input.salesSegment,
            countryCode: input.countryCode,
            salesCategory: STORE_SALES_CATEGORY,
            logoUrl: input.logoUrl.trim() || undefined,
            presentationName: input.presentationName,
            legalName: merchant.name,
            contactInformation: {
                websiteUrl: input.websiteUrl,
                email: input.email.trim(),
                phone: input.phone.trim(),
            },
            remittance: { account: { type: 'Iban', number: input.iban } },
        },
    ],
});

const statusCodesByName: Record<string, number> = {
    OK: 200,
    Accepted: 202,
    Conflict: 409,
    InternalServerError: 500,
};

const toStatusCode = (status: number | string): number =>
    typeof status === 'number' ? status : statusCodesByName[status] ?? Number(status);

const describeStore = (salesSegment: string, countryCode: string) =>
    `${salesSegment} store in ${getOnboardingCountryName(countryCode)}`;

const onboardingError = (code: string, title: string, status: number, detail: string) =>
    new ResponseError([], code, code, title, status, detail, null, {});

const storeAlreadyExistsError = (result: OnboardingStoreResult) =>
    onboardingError(
        'STORE_ALREADY_EXISTS',
        'The store already exists',
        409,
        `A ${describeStore(
            result.salesSegment,
            result.countryCode
        )} already exists for this merchant. Choose another segment or country.`
    );

const storeCreationFailedError = (result: OnboardingStoreResult) =>
    onboardingError(
        'STORE_CREATION_FAILED',
        'The store could not be created',
        500,
        `Walley could not create the ${describeStore(
            result.salesSegment,
            result.countryCode
        )}. Try again, or contact us if it keeps failing.`
    );

const missingResultError = () =>
    onboardingError(
        'STORE_RESULT_MISSING',
        'We could not confirm the store',
        500,
        'The onboarding finished without a result for the store. Refresh the page to check whether it was created before trying again.'
    );

/**
 * Onboards one store for the current user's organization and maps the per-store result
 * (results[0].status) to success or a readable error. Never exposes API credentials.
 */
export const onboardStore = async (
    request: OnboardingRequest,
    onboardStoreStarted: () => void,
    onboardStoreSucceeded: (store: OnboardedStore) => void,
    onboardStoreFailed: (error: ResponseError) => void
) => {
    onboardStoreStarted();

    try {
        const response = await api.postWithResponseType<OnboardingRequest, OnboardingResponse>(
            'onboarding',
            request,
            undefined,
            { timeout: ONBOARDING_REQUEST_TIMEOUT_IN_MILLISECONDS, retries: 0 }
        );

        const [requestedStore] = request.stores;
        const result = response?.results?.[0];

        if (!result) {
            onboardStoreFailed(missingResultError());
            return;
        }

        const status = toStatusCode(result.status);

        if (status === 200 || status === 202) {
            onboardStoreSucceeded({
                id: result.id,
                salesSegment: result.salesSegment ?? requestedStore.salesSegment,
                countryCode: result.countryCode ?? requestedStore.countryCode,
                presentationName: requestedStore.presentationName,
                isPartiallyCreated: status === 202,
            });
            return;
        }

        if (status === 409) {
            onboardStoreFailed(storeAlreadyExistsError(result));
            return;
        }

        onboardStoreFailed(storeCreationFailedError(result));
    } catch (error) {
        onboardStoreFailed(error as ResponseError);
    }
};
